/**
 * ซ้อมทำเงินเดือน — ดูผลคำนวณของงวดที่เลือก "ก่อนรอบจริง" โดยไม่บันทึกอะไรลงระบบ
 *
 * ลูกค้า (TN Riches 22 ก.ย. 2569): "อยากซ้อมทำก่อน เพื่อดูการคำนวณก่อนรอบจริง ไม่เกี่ยวกับเงินเดือน"
 * เส้นนี้เดินสายเดียวกับปุ่ม สรุปเวลา → รันร่าง ของจริงทุกบรรทัด (POST /owner/payroll/rehearse
 * → runReconcileCore/runPayrollCore แบบ dry_run) จึงได้ตัวเลขเดียวกับที่ระบบจะคิดจริง ณ ตอนนี้
 * แต่ไม่แตะสรุปเวลา ร่าง รายการผิดปกติ หรือประวัติการใช้งานเลย — ซ้อมกี่รอบก็ได้ ร่างจริงที่ HR ทำค้างไว้ไม่ถูกทับ
 *
 * หน้าเดียวจบ: เลือกงวด → กดซ้อม → ยอดรวม + ตารางรายคน (กดดูที่มาของค่าจ้าง + บรรทัดสลิป) + คู่มือท้ายหน้า
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { FlaskConical, AlertTriangle, Download, BookOpen, ChevronRight, FileSpreadsheet } from 'lucide-react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { HubHeader } from '../../components/layout/HubHeader';
import { Modal } from '../../components/ui/Modal';
import { useToast } from '../../components/ui/toast';
import { apiDownload } from '../../lib/api-client';
import { useRehearsePayroll, useRehearsalOptions, type RehearsalRow, type RehearsalResult } from '../../hooks';
import { empTypeLabel, type EmpType } from '../../lib/pay-basis';
import { PayBasisBox, DeptPositionFilter, filterByGroup, type PayrollRow } from './Payroll';

const baht = (n: number | null | undefined) =>
  (n ?? 0).toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** "2026-09-02" → "2 ก.ย. 2569" */
function thDate(iso: string): string {
  return new Date(`${iso}T00:00:00Z`).toLocaleDateString('th-TH', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });
}

const nameOf = (r: RehearsalRow) =>
  `${r.first_name ?? ''} ${r.last_name ?? ''}`.trim() + (r.nickname ? ` (${r.nickname})` : '') || r.emp_code;

/** แปลงแถวซ้อมให้ PayBasisBox (ของหน้าเงินเดือนจริง) อ่านได้ — ตัวเลขวันมาจากสรุปที่เพิ่งซ้อม */
function asPayrollRow(r: RehearsalRow, res: RehearsalResult): PayrollRow {
  return {
    id: `rehearsal-${r.emp_code}`,
    emp_code: r.emp_code,
    base_pay: r.base_pay,
    gross: r.gross,
    deductions_total: r.deductions_total,
    net: r.net,
    status: 'draft',
    tax_amount: r.tax_amount,
    sso_amount: r.sso_amount,
    welfare_fund_amount: r.welfare_fund_amount ?? 0,
    first_name: r.first_name,
    last_name: r.last_name,
    nickname: r.nickname,
    bank: null,
    bank_account_masked: null,
    department: r.department,
    position: r.position,
    employee_type: r.employee_type,
    daily_rate: r.daily_rate,
    pay_basis: r.pay_basis,
    summary: r.summary,
    scan_days: r.scan_days,
    offday_scan_days: r.offday_scan_days,
    scan_leave_days: r.scan_leave_days,
    period_start: res.period_start,
    period_end: res.period_end,
  } as PayrollRow;
}

/** fb#129 — รายชั่วโมง: ชั่วโมงที่คิดเงิน + ที่มา ให้เช็คในไฟล์ได้เลย ไม่ต้องเปิดหน้ารันร่าง */
function hoursInfo(r: RehearsalRow) {
  const b = r.pay_basis;
  if (r.employee_type !== 'hourly' || !b) return { hours: '', rate: '', source: '', flagged: '' };
  const source =
    b.hours_source === 'scan' ? `สแกน ${b.scan_days_counted ?? 0} วัน${b.scan_whole_hours ? ' (ชั่วโมงเต็ม)' : ' (นับถึงนาที)'}${b.scan_within_shift ? ` · นับเฉพาะในกะ (ตัดนอกกะ ${b.scan_trimmed_minutes ?? 0} นาที)` : ''}${b.scan_break_deduct ? ` · เกิน 6 ชม. หักพัก 1 ชม. (หักรวม ${(b.scan_break_minutes ?? 0) / 60} ชม.)` : ''}`
    : b.hours_source === 'manual' ? 'กรอกเอง'
    // fb#439 — สวิตช์ปิดแต่สแกนมี: บอกเลขที่สแกนได้และบอกว่ายังไม่ใช้คิด
    : (b.scan_hours_available ?? 0) > 0 ? `ตอนรันสวิตช์ดึงจากสแกนปิด — สแกนได้ ${b.scan_hours_available} ชม. ${b.scan_days_counted ?? 0} วัน ยังไม่ใช้คิด`
    : 'ยังไม่มีชั่วโมง';
  return {
    hours: b.hours_worked ?? '',
    rate: b.hourly_rate ?? r.daily_rate ?? '',
    source,
    flagged: (b.scan_days_flagged ?? []).map((f) => f.date).join(' '),
  };
}

const csvEsc = (v: unknown) => {
  const s = String(v ?? '');
  // กัน CSV injection (=,+,-,@ รวมที่นำหน้าด้วยช่องว่าง/แท็บ/ขึ้นบรรทัด — OWASP) + ครอบด้วย " เมื่อมี , " หรือขึ้นบรรทัดใหม่
  const safe = /^[\s\t\r\n]*[=+\-@]/.test(s) ? `'${s}` : s;
  return /[",\r\n]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe;
};

export function buildCsv(res: RehearsalResult): string {
  const head = ['รหัส', 'ชื่อ', 'ประเภท', 'ตำแหน่ง', 'แผนก', 'วันที่จ่าย', 'ชั่วโมงที่คิดเงิน', 'ค่าจ้าง/ชม.', 'ค่าจ้างฐาน', 'OT', 'เงินเพิ่ม', 'รวมรับ', 'ภาษี', 'ประกันสังคม', 'กองทุน', 'กองทุนสงเคราะห์ลูกจ้าง', 'หักอื่น', 'รวมหัก', 'สุทธิ', 'ที่มาของชั่วโมง', 'วันที่สแกนไม่ครบคู่ (ไม่นับชั่วโมง)'];
  const lines = res.rows.map((r) => {
    const h = hoursInfo(r);
    return [
      r.emp_code, nameOf(r), empTypeLabel(r.employee_type), r.position ?? '', r.department ?? '',
      r.working_days_paid ?? '', h.hours, h.rate, r.base_pay, r.ot_1_pay + r.ot_2_pay + r.ot_3_pay, r.additions_total, r.gross,
      r.tax_amount, r.sso_amount, r.pf_amount, r.welfare_fund_amount ?? 0, r.manual_deductions + r.studentloan_amount + r.companyloan_amount + r.unpaid_leave_deduct + r.absent_deduct,
      r.deductions_total, r.net, h.source, h.flagged,
    ].map(csvEsc).join(',');
  });
  return '\ufeff' + [head.join(','), ...lines].join('\r\n');
}

/** fb#129 — ชั่วโมงรายวันของพนักงานรายชั่วโมง (เวลาเข้า/ออกที่ใช้คิด) หนึ่งแถวต่อคนต่อวัน */
export function buildHoursCsv(res: RehearsalResult): string {
  // fb#492 — ทุกรายงานต้องมีตำแหน่ง
  const head = ['รหัส', 'ชื่อ', 'ตำแหน่ง', 'วันที่', 'เข้า (ที่ใช้คิด)', 'ออก (ที่ใช้คิด)', 'ชั่วโมง', 'หมายเหตุ'];
  const lines: string[] = [];
  for (const r of res.rows) {
    const b = r.pay_basis;
    if (r.employee_type !== 'hourly' || !b) continue;
    const note = b.scan_whole_hours ? 'ชั่วโมงเต็ม: เข้าปัดขึ้น ออกปัดลง' : '';
    // fb#439 — สวิตช์ดึงจากสแกนปิด: ยังลงเวลาเข้า-ออกรายวันให้ดู แต่ติดป้ายว่ายังไม่ใช้คิด (เดิมไฟล์ว่างทั้งคน)
    const unused = b.hours_source === 'none' ? 'ยังไม่ใช้คิดเงิน — ตอนรันร่างสวิตช์ "ดึงชั่วโมงจากการสแกน" ปิด' : '';
    for (const d of b.scan_hours_by_day ?? []) {
      // fb#135 — วันที่ตัดเวลานอกกะ: บอกเวลาสแกนจริงไว้ให้เทียบ
      const early = d.trimmed_min ? `สแกนจริง ${d.scan_in}–${d.scan_out} · นอกกะ ${d.trimmed_min} นาที ไม่นับ` : '';
      // fb#500 — วันที่หักพัก: บอกเวลาที่อยู่จริงก่อนหัก
      const brk = d.break_min ? `อยู่ ${Math.round((d.hours * 60 + d.break_min) / 6) / 10} ชม. หักพัก ${d.break_min} นาที` : '';
      lines.push([r.emp_code, nameOf(r), r.position ?? '', d.date, d.in, d.out, d.hours, [unused, note, early, brk].filter(Boolean).join(' · ')].map(csvEsc).join(','));
    }
    for (const f of b.scan_days_flagged ?? []) {
      lines.push([r.emp_code, nameOf(r), r.position ?? '', f.date, '', '', 0, f.reason === 'outside_shift' ? 'สแกนไม่ตรงกะในตารางเวร — ไม่นับ (แก้ตารางหรือกรอกชั่วโมงเอง)' : 'สแกนไม่ครบคู่/ผิดปกติ — ไม่นับ'].map(csvEsc).join(','));
    }
    if (b.hours_source === 'manual') lines.push([r.emp_code, nameOf(r), r.position ?? '', '', '', '', b.hours_worked ?? '', 'HR กรอกชั่วโมงเองทั้งงวด'].map(csvEsc).join(','));
    lines.push([r.emp_code, nameOf(r), r.position ?? '', 'รวม', '', '', b.hours_worked ?? 0, (b.scan_hours_available ?? 0) > 0 ? `สแกนได้ ${b.scan_hours_available} ชม. แต่ยังไม่ใช้คิด — ตรวจ/เปิด "รายชั่วโมง: ดึงชั่วโมงจากการสแกน" ที่ ค่าจ้าง → รอบจ่ายเงินเดือน แล้วรันร่างใหม่` : ''].map(csvEsc).join(','));
  }
  return '\ufeff' + [head.join(','), ...lines].join('\r\n');
}

export function PayrollRehearsal() {
  const toast = useToast();
  // งวด + ช่วงวันที่มาจาก backend (loadPayCycle ตัวเดียวกับที่คิดเงิน) — จอไม่คำนวณ cutoff เอง (astra รอบ 2:
  // คีย์ที่จอเคยอ่านคนละตัวกับที่เซิร์ฟเวอร์ใช้ + สิทธิ์คนละชั้น → จอบอก 26–25 แต่เซิร์ฟเวอร์คิดเดือนปฏิทิน)
  const { data: opt, isLoading: cycleLoading, error: optError } = useRehearsalOptions();
  const options = useMemo(() => opt?.periods ?? [], [opt]);
  const [period, setPeriod] = useState<string>('');
  const chosen = period || options.find((p) => p.current)?.period || '';
  const chosenOpt = options.find((p) => p.period === chosen) ?? null;
  // ตามค่าบริษัท (ค่าตั้งต้น เหมือนปุ่มรันร่างจริง) · บังคับเงินเดือนล้วน · บังคับคิดตามการลงเวลา
  const [mode, setMode] = useState<'company' | 'salary_only' | 'attendance'>('company');
  const rehearse = useRehearsePayroll();
  // เปลี่ยนงวด/โหมดแล้วผลเก่าต้องหาย ไม่งั้นตาราง/CSV เป็นของงวดก่อน (astra) — แต่ห้าม reset ระหว่างรอผล
  // (ปุ่มถูกล็อกอยู่แล้ว · reset ตอน pending จะปลดล็อกให้กดซ้อนได้)
  const { reset: resetRehearse, isPending } = rehearse;
  const lastKey = useRef(`${chosen}|${mode}`);
  useEffect(() => {
    const key = `${chosen}|${mode}`;
    if (key === lastKey.current) return; // isPending เปลี่ยน ≠ ผู้ใช้เปลี่ยนตัวเลือก (เคย reset ตอนซ้อมเสร็จ → ผลหาย)
    lastKey.current = key;
    if (!isPending) resetRehearse(); // ระหว่างรอผล: ปล่อยให้ res.period === chosen กรองแทน
  }, [chosen, mode, resetRehearse, isPending]);
  const res = rehearse.data && rehearse.data.period === chosen ? rehearse.data : null;

  const [typeFilter, setTypeFilter] = useState<'all' | EmpType>('all');
  const [q, setQ] = useState('');
  // fb#555 — กรองแผนก/ตำแหน่งแบบเดียวกับหน้ารันร่างจริง
  const [deptFilter, setDeptFilter] = useState('');
  const [positionFilter, setPositionFilter] = useState('');
  const [detailFor, setDetailFor] = useState<string | null>(null);

  const rows = useMemo(() => {
    if (!res) return [];
    const needle = q.trim().toLowerCase();
    return filterByGroup(res.rows, deptFilter, positionFilter)
      .filter((r) => typeFilter === 'all' || (r.employee_type ?? 'monthly') === typeFilter)
      .filter((r) => !needle || nameOf(r).toLowerCase().includes(needle) || r.emp_code.toLowerCase().includes(needle) || (r.department ?? '').toLowerCase().includes(needle))
      .sort((a, b) => a.emp_code.localeCompare(b.emp_code));
  }, [res, typeFilter, q, deptFilter, positionFilter]);

  const typeCount = (t: EmpType) => (res?.rows ?? []).filter((r) => (r.employee_type ?? 'monthly') === t).length;

  async function run() {
    if (!chosen) return;
    try {
      const out = await rehearse.mutateAsync({ period: chosen, ...(mode === 'company' ? {} : { salary_only: mode === 'salary_only' }) });
      if (out.period !== chosen) return; // ผู้ใช้เปลี่ยนงวดระหว่างรอ — ผลรอบเก่าไม่ต้องประกาศ
      toast.success(`ซ้อมเสร็จ ${out.employees_processed} คน · ไม่มีอะไรถูกบันทึก`);
      setDetailFor(null);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'ซ้อมไม่สำเร็จ');
    }
  }

  // ไฟล์ชีตเงินเดือน 40 คอลัมน์ (แบบชีตของบริษัท) จากการซ้อม — ส่งให้ฝ่ายค่าคอมกรอก แล้วเอากลับมากรอกตอนรันร่างจริง
  const [sheetBusy, setSheetBusy] = useState(false);
  async function downloadPaysheet() {
    if (!chosen) return;
    setSheetBusy(true);
    try {
      await apiDownload(`/owner/payroll/rehearse/paysheet?period=${encodeURIComponent(chosen)}`, `ชีตเงินเดือน-ซ้อม-${chosen}.csv`);
      toast.success('ดาวน์โหลดไฟล์ชีตเงินเดือนแล้ว');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'สร้างไฟล์ไม่สำเร็จ');
    } finally {
      setSheetBusy(false);
    }
  }

  function saveCsv(text: string, name: string) {
    const blob = new Blob([text], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = name;
    a.click();
    URL.revokeObjectURL(a.href);
  }
  function downloadCsv() {
    if (!res) return;
    saveCsv(buildCsv(res), `ซ้อมเงินเดือน-${res.period}.csv`);
  }
  const hasHourly = !!res?.rows.some((r) => r.employee_type === 'hourly');
  function downloadHoursCsv() {
    if (!res) return;
    saveCsv(buildHoursCsv(res), `ชั่วโมงรายวัน-ซ้อม-${res.period}.csv`);
    toast.success('ดาวน์โหลดชั่วโมงรายวันแล้ว');
  }

  const detailRow = res && detailFor ? res.rows.find((r) => r.emp_code === detailFor) ?? null : null;
  const isFuture = chosenOpt?.future ?? false;

  return (
    <AdminLayout title="ซ้อมทำเงินเดือน">
      <HubHeader
        section="เงินเดือน"
        title="ซ้อมทำเงินเดือน"
        subtitle="ดูว่าระบบจะคิดเงินเดือนงวดนี้ออกมาเท่าไหร่ ณ ตอนนี้ — ไม่บันทึก ไม่แตะร่างจริง ซ้อมกี่รอบก็ได้"
      />

      <div className="card p-4 mb-3 border-brand-100 bg-gradient-to-br from-brand-50 to-white">
        <div className="flex items-start gap-3">
          <FlaskConical className="w-5 h-5 text-brand-700 shrink-0 mt-0.5" />
          <div className="text-sm text-gray-700 space-y-1">
            <p className="font-semibold text-gray-900">หน้านี้คือ "สนามซ้อม"</p>
            <p>
              ระบบจะสรุปเวลาและคิดเงินเดือนด้วยกติกาเดียวกับรอบจริงทุกข้อ (วันมาทำงาน ลา OT เงินเพิ่ม/หัก ภาษี ประกันสังคม)
              แล้วแสดงผลให้ดูเท่านั้น — <b>ไม่บันทึกสรุปเวลา ไม่สร้างร่าง ไม่ตั้งรายการผิดปกติ ไม่ลงประวัติ</b>
              ร่างจริงที่ทำค้างไว้ในเมนู รอบเงินเดือน จึงไม่ถูกทับหรือรีเซ็ต
            </p>
            <p className="text-xs text-gray-500">
              พร้อมทำจริงเมื่อไหร่ ไปที่ <Link to="/owner/cycle" className="text-brand-700 underline">รอบเงินเดือน</Link> แล้วกด สรุปเวลา → รันร่าง ตามปกติ
            </p>
          </div>
        </div>
      </div>

      <div className="card p-4 mb-3">
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-3 items-end">
          <label className="block">
            <span className="label">งวดที่จะซ้อม</span>
            <select className="input w-full" value={chosen} onChange={(e) => setPeriod(e.target.value)} disabled={cycleLoading || !!optError}>
              {options.map((p) => (
                <option key={p.period} value={p.period}>
                  {p.period} · {p.label}{p.future ? ' (ยังไม่จบงวด)' : ''}
                </option>
              ))}
            </select>
            {optError && <span className="text-xs text-red-700">โหลดรอบจ่ายของบริษัทไม่สำเร็จ — ลองใหม่อีกครั้ง</span>}
          </label>
          <button type="button" onClick={run} disabled={rehearse.isPending || !chosen || cycleLoading} className="btn-primary whitespace-nowrap">
            {rehearse.isPending ? 'กำลังซ้อม…' : 'ซ้อมคำนวณงวดนี้'}
          </button>
        </div>
        <label className="block mt-3">
          <span className="label">วิธีคิด</span>
          <select className="input w-full" value={mode} onChange={(e) => setMode(e.target.value as typeof mode)}>
            <option value="company">ตามที่บริษัทตั้งไว้ (เหมือนปุ่มรันร่างจริง)</option>
            <option value="attendance">คิดตามการลงเวลา/ลา/OT (บังคับ)</option>
            <option value="salary_only">เงินเดือนล้วน — ข้ามการลงเวลา จ่ายเต็มตามฐาน (บังคับ)</option>
          </select>
        </label>
        {opt?.calendar_month && (
          <p className="text-xs text-gray-500 mt-2">บริษัทยังไม่ได้ตั้งรอบจ่าย → คิดตามเดือนปฏิทิน (ตั้งได้ที่ ค่าจ้าง → รอบจ่ายเงินเดือน)</p>
        )}
        {isFuture && (
          <p className="text-xs text-amber-700 mt-2">
            งวดนี้ยังมาไม่ถึงวันสุดท้าย — วันที่เหลือ: รายเดือนถือว่ามาทำงานครบ · รายวัน/รายชั่วโมงยังไม่นับ ตัวเลขจะครบเมื่อจบงวด
          </p>
        )}
      </div>

      <div className="card p-4 mb-3">
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-3 items-center">
          <div className="text-sm text-gray-700">
            <p className="font-semibold text-gray-900 flex items-center gap-1.5">
              <FileSpreadsheet className="w-4 h-4 text-brand-700" />ไฟล์ชีตเงินเดือน (แบบชีตของบริษัท)
            </p>
            <p className="text-xs text-gray-500 mt-1">
              เปิดด้วย Excel หรือ Google Sheets ได้ · หน้าตาเดียวกับชีต "พนักงานประจำ (หักประกันสังคม)" ระบบเติมเงินเดือน วันทำงาน OT ขาด/ลา/สาย ให้
              ช่องค่าคอม เงินพิเศษ ความผิดพลาด ค่าเสื้อ ประกันร้าน เว้นว่างไว้ให้กรอก · ดาวน์โหลดไม่บันทึกอะไรลงระบบ
            </p>
            <p className="text-xs text-gray-500 mt-1">
              ไฟล์ซ้อมมีช่องจำนวนวันรายคนต่อท้ายขวาสุด (มาทำงาน · ลาได้ค่าจ้าง · วันหยุด · ขาด · ลาไม่รับเงิน · สาย) ไว้ตรวจก่อนรันร่างจริง — ไม่ต้องวางลงชีต
            </p>
            {isFuture && <p className="text-xs text-amber-700 mt-1">งวดนี้ยังไม่จบ — วันที่เหลือถูกคาดการณ์ ตัวเลขยังไม่ใช่ยอดจริง</p>}
            <p className="text-xs mt-1">
              กรอกเสร็จแล้ว → <Link to="/owner/sheet-import" className="text-brand-700 underline">นำเข้ารายการบวก/หักจากไฟล์ชีต</Link> อัปโหลดไฟล์ครั้งเดียว ยอดเพิ่ม/หักของทุกคนเข้าร่างจริง ไม่ต้องกรอกทีละคน
            </p>
          </div>
          <button type="button" onClick={() => void downloadPaysheet()} disabled={sheetBusy || !chosen || cycleLoading} className="btn-secondary whitespace-nowrap">
            <Download className="w-4 h-4 inline mr-1" />
            {sheetBusy ? 'กำลังสร้างไฟล์…' : `ดาวน์โหลดงวด ${chosen || ''}`}
          </button>
        </div>
      </div>

      {rehearse.isPending && (
        <div className="space-y-2 mb-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="card p-3">
              <div className="h-4 bg-gray-100 rounded w-1/3 animate-pulse mb-2" />
              <div className="h-3 bg-gray-100 rounded w-2/3 animate-pulse" />
            </div>
          ))}
        </div>
      )}

      {res && !rehearse.isPending && (
        <>
          <div className="card p-3 mb-3 bg-emerald-50 border border-emerald-200 text-emerald-900 text-xs">
            ซ้อมเสร็จ {new Date(res.computed_at).toLocaleString('th-TH', { timeZone: 'Asia/Bangkok' })} · งวด {res.period} ({thDate(res.period_start)} – {thDate(res.period_end)}) · {res.employees_processed} คน · ใช้เวลา {(res.elapsed_ms / 1000).toFixed(1)} วิ · <b>ไม่มีอะไรถูกบันทึก</b>
          </div>
          {res.projection_warning && (
            <div className="card p-3 mb-3 bg-amber-50 border border-amber-200 text-amber-900 text-xs flex gap-2">
              <AlertTriangle className="w-4 h-4 shrink-0" />
              <span>{res.projection_warning}</span>
            </div>
          )}

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
            <div className="card p-3"><p className="text-xs text-gray-500">รวมรับ</p><p className="text-lg font-bold text-gray-900">฿{baht(res.totals.gross_sum)}</p></div>
            <div className="card p-3"><p className="text-xs text-gray-500">รวมหัก</p><p className="text-lg font-bold text-red-700">฿{baht(res.totals.deductions_sum)}</p></div>
            <div className="card p-3"><p className="text-xs text-gray-500">ยอดโอนสุทธิ</p><p className="text-lg font-bold text-emerald-700">฿{baht(res.totals.net_sum)}</p></div>
            <div className="card p-3">
              <p className="text-xs text-gray-500">ต้องจัดการก่อนปิดงวดจริง</p>
              <p className="text-lg font-bold text-amber-700">{res.unresolved_employees} คน</p>
              <p className="text-[10px] text-gray-400">รายการผิดปกติที่จะถูกตั้ง {res.escalations_found} รายการ (ยังไม่ได้ตั้งจริง)</p>
            </div>
          </div>

          {res.warnings.length > 0 && (
            <div className="card p-3 mb-3 text-xs">
              <p className="font-semibold text-gray-800 mb-1">ข้อสังเกตจากระบบ ({res.warnings.length})</p>
              <ul className="space-y-0.5 text-gray-700">
                {res.warnings.slice(0, 30).map((w, i) => (
                  <li key={`${w.emp_code}-${w.type}-${i}`}>• {w.emp_code}: {w.label}{w.prev_amount != null ? ` (งวดก่อน ฿${baht(w.prev_amount)})` : ''}</li>
                ))}
                {res.warnings.length > 30 && <li className="text-gray-400">…และอีก {res.warnings.length - 30} รายการ</li>}
              </ul>
            </div>
          )}

          <div className="card p-3 mb-3">
            <div className="flex flex-wrap gap-2 items-center mb-2">
              {(['all', 'monthly', 'daily', 'hourly'] as const).map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setTypeFilter(t)}
                  className={`px-3 py-1 rounded-full text-xs whitespace-nowrap ${typeFilter === t ? 'bg-brand-100 text-brand-700 font-medium' : 'bg-white border border-gray-200 text-gray-600'}`}
                >
                  {t === 'all' ? `ทั้งหมด ${res.rows.length}` : `${empTypeLabel(t)} ${typeCount(t)}`}
                </button>
              ))}
              <input className="input text-sm flex-1 min-w-[160px]" placeholder="ค้นหาชื่อ / รหัส / แผนก" value={q} onChange={(e) => setQ(e.target.value)} />
              <button type="button" onClick={downloadCsv} className="btn-secondary text-xs whitespace-nowrap">
                <Download className="w-3.5 h-3.5 inline mr-1" />CSV
              </button>
              {hasHourly && (
                <button type="button" onClick={downloadHoursCsv} className="btn-secondary text-xs whitespace-nowrap">
                  <Download className="w-3.5 h-3.5 inline mr-1" />ชั่วโมงรายวัน
                </button>
              )}
            </div>
            <div className="-mx-3 mb-2 border-t">
              <DeptPositionFilter rows={res.rows} dept={deptFilter} setDept={setDeptFilter} position={positionFilter} setPosition={setPositionFilter} />
            </div>
            <div className="overflow-x-auto -mx-3 px-3">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-gray-500 border-b border-gray-200">
                    <th className="text-left py-2 pr-2 font-medium">พนักงาน</th>
                    <th className="text-left py-2 pr-2 font-medium whitespace-nowrap">ประเภท</th>
                    <th className="text-right py-2 pr-2 font-medium whitespace-nowrap">วันที่จ่าย</th>
                    <th className="text-right py-2 pr-2 font-medium whitespace-nowrap">ค่าจ้างฐาน</th>
                    <th className="text-right py-2 pr-2 font-medium">OT</th>
                    <th className="text-right py-2 pr-2 font-medium whitespace-nowrap">เงินเพิ่ม</th>
                    <th className="text-right py-2 pr-2 font-medium whitespace-nowrap">รวมหัก</th>
                    <th className="text-right py-2 font-medium">สุทธิ</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.emp_code} className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer" onClick={() => setDetailFor(r.emp_code)}>
                      <td className="py-2 pr-2">
                        <div className="font-medium text-gray-900 flex items-center gap-1">
                          {nameOf(r)}
                          {r.summary?.has_unresolved && <AlertTriangle className="w-3 h-3 text-amber-600" aria-label="มีรายการต้องจัดการ" />}
                        </div>
                        <div className="text-[10px] text-gray-400">{r.emp_code}{r.department ? ` · ${r.department}` : ''}</div>
                      </td>
                      <td className="py-2 pr-2 whitespace-nowrap">{empTypeLabel(r.employee_type)}</td>
                      <td className="py-2 pr-2 text-right">
                        {r.working_days_paid ?? '—'}
                        {r.employee_type === 'hourly' && r.pay_basis?.hours_worked != null && (
                          <div className="text-[10px] text-gray-500 whitespace-nowrap">{r.pay_basis.hours_worked.toLocaleString('th-TH')} ชม.</div>
                        )}
                      </td>
                      <td className="py-2 pr-2 text-right">{baht(r.base_pay)}</td>
                      <td className="py-2 pr-2 text-right">{baht(r.ot_1_pay + r.ot_2_pay + r.ot_3_pay)}</td>
                      <td className="py-2 pr-2 text-right">{baht(r.additions_total)}</td>
                      <td className="py-2 pr-2 text-right text-red-700">{baht(r.deductions_total)}</td>
                      <td className="py-2 text-right font-semibold text-emerald-700">{baht(r.net)}</td>
                    </tr>
                  ))}
                  {rows.length === 0 && (
                    <tr><td colSpan={8} className="py-6 text-center text-gray-400">ไม่มีรายการที่ตรงกับตัวกรอง</td></tr>
                  )}
                </tbody>
              </table>
            </div>
            <p className="text-[10px] text-gray-400 mt-2">แตะแถวเพื่อดูที่มาของค่าจ้างและบรรทัดสลิปของคนนั้น · ตัวเลขทั้งหมดเป็นการซ้อม ยังไม่ใช่ยอดจ่ายจริง</p>
          </div>
        </>
      )}

      <RehearsalGuide />

      <Modal open={!!detailRow} onClose={() => setDetailFor(null)} title={detailRow ? `${nameOf(detailRow)} · ซ้อมงวด ${res?.period ?? ''}` : ''} size="max-w-lg">
        {detailRow && res && (
          <div className="space-y-3 text-sm">
            <div className="text-xs bg-amber-50 border border-amber-200 text-amber-900 rounded-lg p-2">ตัวเลขจากการซ้อม — ไม่ใช่ยอดจ่ายจริง และไม่ได้บันทึกไว้ที่ไหน</div>
            <PayBasisBox row={asPayrollRow(detailRow, res)} />
            <div className="grid grid-cols-2 gap-3">
              <div>
                <p className="text-xs font-semibold text-gray-700 mb-1">รายรับ</p>
                <ul className="text-xs space-y-0.5">
                  {(detailRow.line_items?.earnings ?? []).map((l, i) => (
                    <li key={`e-${i}`} className="flex justify-between gap-2"><span className="text-gray-600">{l.label}</span><span>{baht(l.amount)}</span></li>
                  ))}
                  <li className="flex justify-between gap-2 border-t border-gray-200 pt-1 font-semibold"><span>รวมรับ</span><span>{baht(detailRow.line_items?.earnings_total ?? detailRow.gross)}</span></li>
                </ul>
              </div>
              <div>
                <p className="text-xs font-semibold text-gray-700 mb-1">รายการหัก</p>
                <ul className="text-xs space-y-0.5">
                  {(detailRow.line_items?.deductions ?? []).map((l, i) => (
                    <li key={`d-${i}`} className="flex justify-between gap-2"><span className="text-gray-600">{l.label}</span><span>{baht(l.amount)}</span></li>
                  ))}
                  <li className="flex justify-between gap-2 border-t border-gray-200 pt-1 font-semibold"><span>รวมหัก</span><span>{baht(detailRow.line_items?.deductions_total ?? detailRow.deductions_total)}</span></li>
                </ul>
              </div>
            </div>
            <div className="flex justify-between text-base font-bold border-t border-gray-200 pt-2">
              <span>สุทธิ (ถ้าจ่ายตอนนี้)</span><span className="text-emerald-700">฿{baht(detailRow.net)}</span>
            </div>
            {detailRow.summary?.has_unresolved && (
              <p className="text-xs text-amber-800">คนนี้มีรายการผิดปกติที่ต้องจัดการก่อนปิดงวดจริง (ขาด/สาย/สแกนไม่ครบ) — ดูรายวันที่ ประวัติเข้างานรายคน</p>
            )}
          </div>
        )}
      </Modal>
    </AdminLayout>
  );
}

/** คู่มือสั้นท้ายหน้า — พับได้ อ่านจบใน 1 นาที (ฉบับเต็มอยู่ในเมนูคู่มือของระบบ ERP ของบริษัท) */
function RehearsalGuide() {
  const [open, setOpen] = useState(false);
  return (
    <div className="card p-3 mb-3">
      <button type="button" className="w-full flex items-center justify-between" onClick={() => setOpen((v) => !v)}>
        <span className="font-medium text-gray-900 flex items-center gap-1.5 text-sm">
          <BookOpen className="w-4 h-4 text-brand-700" /> คู่มือ: ซ้อมทำเงินเดือนยังไง และอ่านผลยังไง
        </span>
        <ChevronRight className={`w-4 h-4 text-gray-400 transition-transform ${open ? 'rotate-90' : ''}`} />
      </button>
      {open && (
        <div className="mt-3 text-sm text-gray-700 space-y-3">
          <div>
            <p className="font-semibold text-gray-900">ซ้อมเมื่อไหร่ดี</p>
            <ul className="list-disc pl-5 space-y-0.5">
              <li><b>กลางงวด</b> — ดูว่าถึงตอนนี้ใครขาด/สาย/สแกนไม่ครบ จะได้ตามแก้ก่อนถึงวันปิดงวด</li>
              <li><b>ก่อนปิดงวด 1–2 วัน</b> — ดูยอดโอนรวมและตรวจคนที่ตัวเลขผิดตา ก่อนกดสรุปเวลา/รันร่างจริง</li>
              <li><b>หลังแก้ข้อมูล</b> (ลงเวลาย้อนหลัง ยกเลิกใบลา ใส่เงินเพิ่ม/หัก) — ซ้อมซ้ำเพื่อดูว่าตัวเลขเปลี่ยนตามที่ตั้งใจไหม</li>
            </ul>
          </div>
          <div>
            <p className="font-semibold text-gray-900">ขั้นตอน</p>
            <ol className="list-decimal pl-5 space-y-0.5">
              <li>เลือก <b>งวดที่จะซ้อม</b> (ค่าตั้งต้น = งวดปัจจุบันตามรอบจ่ายของบริษัท)</li>
              <li>กด <b>ซ้อมคำนวณงวดนี้</b> รอไม่กี่วินาที</li>
              <li>ดูกล่องยอดรวม 4 ช่อง แล้วไล่ตารางรายคน — แตะแถวเพื่อดู "วิธีคิดค่าจ้าง" (เรท × วัน) และบรรทัดสลิป</li>
              <li>อยากเก็บไว้เทียบ กด <b>CSV</b> เปิดใน Excel ได้ทันที</li>
            </ol>
          </div>
          <div>
            <p className="font-semibold text-gray-900">อ่านผลยังไง</p>
            <ul className="list-disc pl-5 space-y-0.5">
              <li><b>วันที่จ่าย</b> = วันที่เอาไปคูณเรท (รายวัน) หรือวันมาทำงาน+ลาได้ค่าจ้าง+วันหยุด (รายเดือน — เป็นข้อมูลประกอบ ไม่ใช่ตัวคูณ)</li>
              <li><b>รายวัน</b> คิดจากวันที่สแกนเข้างานจริง + วันลาที่ได้ค่าจ้าง + วันหยุดนักขัตฤกษ์ — ไม่จ่ายวันหยุดประจำสัปดาห์ และวันที่ยังมาไม่ถึงยังไม่นับ</li>
              <li><b>รายเดือน</b> จ่ายตามฐานเงินเดือน หักเฉพาะวันขาด/ลาไม่รับค่าจ้าง — วันที่ยังมาไม่ถึงถือว่ามาทำงานครบ</li>
              <li>สามเหลี่ยมเหลืองหน้าชื่อ = คนนี้มีเรื่องค้าง (ขาดไม่มีหลักฐาน / สาย / สแกนไม่ครบ) — ตอนทำจริงระบบจะตั้งเป็น "กิจกรรมผิดปกติ" ให้จัดการก่อนปิดงวด</li>
            </ul>
          </div>
          <div>
            <p className="font-semibold text-gray-900">ซ้อมกับของจริงต่างกันตรงไหน</p>
            <ul className="list-disc pl-5 space-y-0.5">
              <li>ซ้อม: <b>ไม่บันทึกอะไรเลย</b> — สรุปเวลา ร่าง กิจกรรมผิดปกติ ประวัติการใช้งาน ไม่มีอะไรเปลี่ยน กดกี่รอบก็ได้</li>
              <li>ของจริง (เมนู รอบเงินเดือน): สรุปเวลา → รันร่าง → ตรวจ → อนุมัติ → ล็อกงวด → ส่งสลิป ทุกขั้นบันทึกและมีประวัติ</li>
              <li>ตัวเลขซ้อมกับร่างจริง ณ เวลาเดียวกันจะ <b>เท่ากัน</b> เพราะใช้สูตรชุดเดียวกัน — ถ้าไม่เท่ากัน แปลว่าร่างจริงทำไว้ก่อนข้อมูลเปลี่ยน ให้กดสรุปเวลา/รันร่างใหม่</li>
              <li>งวดที่ปิดจ่ายไปแล้วซ้อมไม่ได้ (ตัวเลขจริงดูที่ รอบเงินเดือน)</li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}
